import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Switch, FormControlLabel, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useOrderSettings from "../../hooks/useOrderSettings";

const getInitialValues = (settings) => ({
  enabled: settings?.status === "ENABLED",
  returnPeriod: settings?.return_period || "",
  isCodAllowed: settings?.is_cod_allowed === true || settings?.is_cod_allowed === "1",
  codValue: settings?.cod_value || "",
  minimumCartValue: settings?.minimum_cart_value || "",
});

const OrderSettings = () => {
  const navigate = useNavigate();

  // React Hook Form for B2B
  const {
    register: registerB2B,
    handleSubmit: handleSubmitB2B,
    setValue: setValueB2B,
    watch: watchB2B,
  } = useForm({
    defaultValues: getInitialValues({}),
    mode: "onChange",
  });

  // React Hook Form for B2C
  const {
    register: registerB2C,
    handleSubmit: handleSubmitB2C,
    setValue: setValueB2C,
    watch: watchB2C,
  } = useForm({
    defaultValues: getInitialValues({}),
    mode: "onChange",
  });

  // IDs for each form, managed in state
  const [b2bIds, setB2bIds] = useState({ order_settings_id: "", company_settings_id: "" });
  const [b2cIds, setB2cIds] = useState({ order_settings_id: "", company_settings_id: "" });

  // Use main hook and set form values in onSuccess
  const { saving, saveOrderSettings } = useOrderSettings({
    onSuccess: (data) => {
      const b2b = data?.b2b && !Array.isArray(data.b2b) ? data.b2b : {};
      const b2c = data?.b2c && !Array.isArray(data.b2c) ? data.b2c : {};
      setValueB2B("enabled", b2b.status === "ENABLED");
      setValueB2B("returnPeriod", b2b.return_period || "");
      setValueB2B("isCodAllowed", b2b.is_cod_allowed === true || b2b.is_cod_allowed === "1");
      setValueB2B("codValue", b2b.cod_value || "");
      setValueB2B("minimumCartValue", b2b.minimum_cart_value || "");
      setValueB2C("enabled", b2c.status === "ENABLED");
      setValueB2C("returnPeriod", b2c.return_period || "");
      setValueB2C("isCodAllowed", b2c.is_cod_allowed === true || b2c.is_cod_allowed === "1");
      setValueB2C("codValue", b2c.cod_value || "");
      setValueB2C("minimumCartValue", b2c.minimum_cart_value || "");
      setB2bIds({
        order_settings_id: b2b.order_settings_id || "",
        company_settings_id: b2b.company_settings_id || "",
      });
      setB2cIds({
        order_settings_id: b2c.order_settings_id || "",
        company_settings_id: b2c.company_settings_id || "",
      });
    },
    onSaveSuccess: ({ order_settings_id, company_settings_id, settings_type }) => {
      // Only update the ids for the form that was just saved
      if (settings_type === "B2B") {
        setB2bIds({
          order_settings_id: order_settings_id || "",
          company_settings_id: company_settings_id || "",
        });
      } else if (settings_type === "B2C") {
        setB2cIds({
          order_settings_id: order_settings_id || "",
          company_settings_id: company_settings_id || "",
        });
      }
    }
  });

  // Save handlers
  const onSubmitB2B = (data) => {
    saveOrderSettings({
      ...data,
      settings_type: "B2B",
      order_settings_id: b2bIds.order_settings_id || "",
      company_settings_id: b2bIds.company_settings_id || "",
    });
  };

  const onSubmitB2C = (data) => {
    saveOrderSettings({
      ...data,
      settings_type: "B2C",
      order_settings_id: b2cIds.order_settings_id || "",
      company_settings_id: b2cIds.company_settings_id || "",
    });
  };

  const enabledB2B = watchB2B("enabled");
  const enabledB2C = watchB2C("enabled");

  return (
    <div className="container_xxl min-h-screen bg-gradient-to-br from-[#f5f7fa] to-[#c3cfe2] px-4">
      <div className="flex items-center space-x-4 headingBorder__b sticky top-0 z-10">
        <div>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </div>
        <div>
          <h1 className="heading">Orders Settings</h1>
        </div>
      </div>
      <div className="py-5 flex flex-col gap-8">
        {/* B2B Settings */}
        <section>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <span>B2B Order Settings</span>
            </h2>
            <form onSubmit={handleSubmitB2B(onSubmitB2B)}>
              <div className="flex items-center justify-between mb-8">
                <span className="text-lg font-medium text-gray-800">
                  Enable Return Period
                </span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabledB2B}
                      onChange={(e) => setValueB2B("enabled", e.target.checked)}
                      color="primary"
                    />
                  }
                  label={enabledB2B ? "Enabled" : "Disabled"}
                  className="m-0"
                />
              </div>
              {enabledB2B && (
                <div className="mb-8">
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Return Period (days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 7"
                    {...registerB2B("returnPeriod")}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-lg"
                    onWheel={e => e.target.blur()}
                  />
                  <span className="text-xs text-gray-400 mt-1 block">
                    Set the number of days customers can return their orders.
                  </span>
                  {/* remove errorsB2B.returnPeriod */}
                </div>
              )}
              <div className="mb-8">
                <FormControlLabel
                  control={
                    <input
                      type="checkbox"
                      checked={!!watchB2B("isCodAllowed")}
                      onChange={(e) => setValueB2B("isCodAllowed", e.target.checked)}
                      className="mr-2"
                    />
                  }
                  label="Enable cash on delivery"
                  className="m-0"
                />
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      Cash on delivery charges
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="e.g. 1000"
                      {...registerB2B("codValue")}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-lg"
                      onWheel={e => e.target.blur()}
                    />
                  </div>
                  {/* Empty column for spacing */}
                  <div className="col-span-2 md:col-span-1"></div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      Minimum cart value
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="e.g. 1000"
                      {...registerB2B("minimumCartValue")}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-lg"
                      onWheel={e => e.target.blur()}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={saving}
                  style={{ borderRadius: "0.75rem", minWidth: 120 }}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          </div>
        </section>
        {/* B2C Settings */}
        <section>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <span>B2C Order Settings</span>
            </h2>
            <form onSubmit={handleSubmitB2C(onSubmitB2C)}>
              <div className="flex items-center justify-between mb-8">
                <span className="text-lg font-medium text-gray-800">
                  Enable Return Period
                </span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabledB2C}
                      onChange={(e) => setValueB2C("enabled", e.target.checked)}
                      color="primary"
                    />
                  }
                  label={enabledB2C ? "Enabled" : "Disabled"}
                  className="m-0"
                />
              </div>
              {enabledB2C && (
                <div className="mb-8">
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Return Period (days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 7"
                    {...registerB2C("returnPeriod")}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-lg"
                    onWheel={e => e.target.blur()}
                  />
                  <span className="text-xs text-gray-400 mt-1 block">
                    Set the number of days customers can return their orders.
                  </span>
                </div>
              )}
              <div className="mb-8">
                <FormControlLabel
                  control={
                    <input
                      type="checkbox"
                      checked={!!watchB2C("isCodAllowed")}
                      onChange={(e) => setValueB2C("isCodAllowed", e.target.checked)}
                      className="mr-2"
                    />
                  }
                  label="Enable cash on delivery"
                  className="m-0"
                />
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      Cash on delivery charges
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="e.g. 1000"
                      {...registerB2C("codValue")}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-lg"
                      onWheel={e => e.target.blur()}
                    />
                  </div>
                  {/* Empty column for spacing */}
                  <div className="col-span-2 md:col-span-1"></div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      Minimum cart value
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="e.g. 1000"
                      {...registerB2C("minimumCartValue")}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-lg"
                      onWheel={e => e.target.blur()}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={saving}
                  style={{ borderRadius: "0.75rem", minWidth: 120 }}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OrderSettings;
